<template>
  <div>
    <!-- Sort Controls -->
    <div class="sort-controls mb-3">
      <span class="sort-label">Ordenar per:</span>
      <button
        v-for="column in sortableColumns"
        :key="column.id"
        @click="sortBy(column.id)"
        :class="['btn', 'btn-sm', 'mx-1', getSortButtonClass(column.id)]"
        :title="`Ordenar per ${$t(column.name)}`"
      >
        {{ $t(column.name) }}
        <span v-if="sortField === column.id" class="sort-icon">
          {{ sortOrder === "asc" ? "↑" : "↓" }}
        </span>
      </button>
      <button
        v-if="sortField"
        @click="clearSort()"
        class="btn btn-outline-secondary btn-sm mx-1"
        title="Limpiar ordenación"
      >
        Limpiar
      </button>
    </div>
    <vue-data-table
      :tableColumns="columnsWithSort"
      :tableRows="rows"
      :isLoading="isLoading"
      :showRowTableAction="false"
      :empty-rows-message="$t('components.table.no_data')"
      @[needDataUpdateEvent]="init"
    >
      <template #table-header="props">
        <th
          :style="props.column.styleHeader"
          :class="props.column.classHeader"
          @click="sortBy(props.column.id)"
          style="cursor: pointer; user-select: none"
          :title="`Click to sort by ${$t(props.column.name)}`"
        >
          <span v-if="props.column.useTranslate">{{ $t(props.column.name) }}</span>
          <span v-else>{{ props.column.name }}dd</span>
          <span v-if="sortField === props.column.id" class="sort-indicator">
            {{ sortOrder === "asc" ? " ↑" : " ↓" }}
          </span>
        </th>
      </template>
      <template #table-row="props">
        <template v-if="['dateEnd', 'dateStart'].includes(props.cell.getColumn.id)">
          <formatted-date-component :val="props.cell.getValue" :withTime="true" />
        </template>
        <template
          v-else-if="
            ['causeOfAbsenceDescription', 'statusDescription'].includes(props.cell.getColumn.id)
          "
        >
          <a
            href="#"
            @click.prevent="showDetail(props.row.getRaw)"
            v-if="props.row.getRaw.isConfirmWaiting()"
          >
            {{ props.cell.getValue }}
          </a>
          <div v-else>
            {{ props.cell.getValue }}
          </div>
        </template>
        <template v-else-if="['attachments'].includes(props.cell.getColumn.id)">
          <div v-if="props.cell.getValue.length > 0">
            <div
              class="description"
              v-for="attachment in props.cell.getValue"
              :key="attachment.key"
            >
              <a target="_blank" href="#" @click.prevent="downLoadAttachment(attachment)">
                {{ attachment.name }}
              </a>
            </div>
          </div>
          <div v-else>&nbsp;</div>
        </template>
        <template v-else>
          <span>{{ props.cell.getValue }} </span>
        </template>
      </template>
    </vue-data-table>

    <revision-modal />
  </div>
</template>

<script>
import VueDataTable, { Column, VUE_DATA_TABLE_NEED_DATA_UPDATE } from "@/components/table";
import { SHOW_ABSENCE_REVISION_ADD_MODAL } from "@/pages/responsible/planing/data/events";
import { FormattedDateComponent } from "@/components/formatted";
import api from "@/api/appointment";

import { ABSENCES_SEARCH_CHANGED_EVENT } from "./index";
import { FinalAbsence } from "@/pages/components/data";
import RevisionModal from "@/pages/responsible/planing/absences/components/RevisionModal.vue";

import { ModalAlert } from "@/components/modal";
import { ABSENCE_UPDATED_EVENT } from "@/pages/employee/permission/absences/components";

export default {
  components: {
    VueDataTable,
    FormattedDateComponent,
    RevisionModal,
  },
  data() {
    return {
      columns: [
        new Column({
          id: "id",
          name: "pages.planing-team.absences.id",
        }),
        new Column({
          id: "employeeName",
          name: "pages.planing-team.absences.employee",
          styleHeader: {
            "min-width": "200px !important",
            "max-width": "200px !important",
            "text-align": "center",
          },
        }),
        new Column({
          id: "causeOfAbsenceDescription",
          name: "pages.planing-team.absences.type_absenses",
        }),
        new Column({
          id: "statusDescription",
          name: "pages.planing-team.absences.status",
        }),
        new Column({
          id: "comments",
          name: "pages.planing-team.absences.comments",
          styleHeader: {
            "min-width": "130px !important",
            "max-width": "130px !important",
            "text-align": "center",
          },
        }),
        new Column({
          id: "dateStart",
          name: "pages.planing-team.absences.start_date",
          styleHeader: {
            "min-width": "130px !important",
            "max-width": "130px !important",
            "text-align": "center",
          },
        }),
        new Column({
          id: "dateEnd",
          name: "pages.planing-team.absences.end_date",
          styleHeader: {
            "min-width": "130px !important",
            "max-width": "130px !important",
            "text-align": "center",
          },
        }),
        new Column({
          id: "attachments",
          name: "pages.planing-team.absences.attachments",
          styleHeader: {
            "min-width": "130px !important",
            "max-width": "130px !important",
            "text-align": "center",
          },
        }),
      ],
      rows: [],
      originalRows: [],
      isLoading: false,
      employeeCode: null,
      status: null,
      departmentCode: null,
      establishmentCode: null,
      sortField: null,
      sortOrder: "asc", // 'asc' or 'desc'
    };
  },
  computed: {
    needDataUpdateEvent() {
      return VUE_DATA_TABLE_NEED_DATA_UPDATE;
    },
    columnsWithSort() {
      return this.columns;
    },
    sortableColumns() {
      // Define which columns are sortable
      return this.columns.filter((column) => {
        // Exclude attachments column from sorting as it's more complex
        return column.id !== "attachments";
      });
    },
  },
  created() {
    EventBus.listen(ABSENCES_SEARCH_CHANGED_EVENT, (args) => {
      console.log("=== ABSENCES LIST EVENT RECEIVED ===");
      console.log("Search args:", args);
      this.employeeCode = args.employeeCode;
      this.status = args.status;
      this.departmentCode = args.department;
      this.establishmentCode = args.establishment;
      console.log("Updated filters:", {
        employeeCode: this.employeeCode,
        status: this.status,
        departmentCode: this.departmentCode,
        establishmentCode: this.establishmentCode,
      });
      this.init();
    });
    EventBus.listen(ABSENCE_UPDATED_EVENT, () => {
      this.init();
    });
    this.init();
  },
  beforeDestroy() {
    EventBus.off([ABSENCES_SEARCH_CHANGED_EVENT, ABSENCE_UPDATED_EVENT]);
  },
  methods: {
    async init() {
      this.isLoading = true;
      const apiParams = {
        employeeCode: this.employeeCode,
        status: this.status,
        departmentCode: this.departmentCode,
        establishmentCode: this.establishmentCode,
      };
      console.log("=== ABSENCES API CALL ===");
      console.log("API Parameters:", apiParams);
      const { data } = await api.getFilteredAbsences(apiParams);
      this.originalRows = data.map((x) => new FinalAbsence().parse(x));
      this.rows = [...this.originalRows];
      this.applySorting();
      this.isLoading = false;
    },
    getSortButtonClass(columnId) {
      if (this.sortField === columnId) {
        return "btn-primary";
      }
      return "btn-outline-primary";
    },
    clearSort() {
      console.log("Clearing sort");
      this.sortField = null;
      this.sortOrder = "asc";
      this.rows = [...this.originalRows];
    },
    sortBy(columnId) {
      console.log("Sorting by column:", columnId);
      if (this.sortField === columnId) {
        // Toggle sort order if same column
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        // New column, default to ascending
        this.sortField = columnId;
        this.sortOrder = "asc";
      }
      this.applySorting();
    },
    applySorting() {
      if (!this.sortField) {
        this.rows = [...this.originalRows];
        return;
      }

      this.rows = [...this.originalRows].sort((a, b) => {
        let valueA = this.getNestedValue(a, this.sortField);
        let valueB = this.getNestedValue(b, this.sortField);

        // Handle null/undefined values
        if (valueA == null && valueB == null) return 0;
        if (valueA == null) return 1;
        if (valueB == null) return -1;

        let result;
        // Special handling for different field types
        if (this.sortField === "id") {
          // Numeric sorting for ID field
          const numA = parseInt(valueA, 10);
          const numB = parseInt(valueB, 10);
          if (!isNaN(numA) && !isNaN(numB)) {
            result = numA - numB;
          } else {
            // Fallback to string comparison if not numeric
            result = String(valueA).localeCompare(String(valueB));
          }
        } else if (this.sortField === "dateStart" || this.sortField === "dateEnd") {
          // Date sorting
          const dateA = new Date(valueA);
          const dateB = new Date(valueB);
          if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
            result = dateA.getTime() - dateB.getTime();
          } else {
            // Fallback to string comparison
            result = String(valueA).localeCompare(String(valueB));
          }
        } else {
          // String sorting (case-insensitive)
          result = String(valueA).toLowerCase().localeCompare(String(valueB).toLowerCase());
        }

        return this.sortOrder === "desc" ? -result : result;
      });
      console.log("Sorted", this.rows.length, "rows by", this.sortField, this.sortOrder);
    },
    getSortType() {
      if (this.sortField === "id") return "numeric";
      if (this.sortField === "dateStart" || this.sortField === "dateEnd") return "date";
      return "text";
    },
    getNestedValue(obj, path) {
      // Handle nested properties like 'employee.name'
      return path.split(".").reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    },
    showDetail(finalAbsence) {
      EventBus.fire(SHOW_ABSENCE_REVISION_ADD_MODAL, { appointment: finalAbsence });
    },
    downLoadAttachment(attachment) {
      this.isLoading = true;
      api
        .downloadAttachment({
          employeeCode: attachment.employeeCode,
          reference: attachment.reference,
        })
        .then((response) => {
          const type = response.headers["content-type"];
          const blob = new Blob([response.data], { type: type, encoding: "UTF-8" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `${attachment.name}.${attachment.fileExtension}`;
          link.click();
          this.isLoading = false;
        })
        .catch((error) => {
          ModalAlert.error({ message: "Error" });
          console.error(error.response);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
.sort-controls {
  padding: 10px;
  background-color: var(--portal-surface-muted);
  border-radius: 10px;
  border: 1px solid var(--portal-border);
  color: var(--portal-text);
}

.sort-label {
  font-weight: 500;
  margin-right: 10px;
  color: var(--portal-text-muted);
}

.sort-icon {
  font-weight: bold;
  margin-left: 5px;
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
}
</style>
